import {
  WixPatternsContainer,
  CollectionState,
  FiltersMap,
  SelectState,
  TaskState,
  TypedEmitter,
  withoutDefaults,
} from '@wix/bex-core';
import { action, computed, makeObservable, reaction } from 'mobx';
import { ToolbarCollectionState } from './ToolbarCollectionState';
import { Column } from '../model';
import type { CustomColumnsDragAndDrop } from '../components/CustomColumns/CustomColumnsDragAndDrop';
import { CustomColumnsHistoryState } from './CustomColumnsHistoryState';
import { indexOfCompareFn } from './indexOfCompareFn';
import { EventEmitter } from 'events';
import { cairoCustomizeColumnsInSidePanel } from '@wix/bex-core/bi';

type StoredColumn =
  | { id?: string; name?: string; isSelected?: boolean }
  | null
  | undefined;

const asStoredColumnsOrNull = (customColumnsResult: unknown) =>
  Array.isArray(customColumnsResult)
    ? (customColumnsResult as StoredColumn[])
    : null;

export interface CustomColumnsStateParams<T, F extends FiltersMap> {
  readonly table: ToolbarCollectionState<T, F>;
  readonly container: WixPatternsContainer;
  readonly dragAndDrop?: typeof CustomColumnsDragAndDrop;
}

export type CustomColumnsActionName = 'visibility' | 'reorder';

export class CustomColumnsState<T, F extends FiltersMap> {
  readonly table;
  readonly container;

  readonly columnsCollection;

  readonly dragAndDrop;

  readonly customColumnsHistory;

  onColumnClick?: (column: Column, checked: boolean) => unknown;

  _didFirstColumnsMerge?: boolean;
  _performedCustomColumnsActions: Map<CustomColumnsActionName, boolean> =
    new Map();

  readonly initTask = new TaskState();

  readonly select = new SelectState<{ id: string }>({
    keyGetter: (column) => column.id,
  });

  readonly events = new EventEmitter() as TypedEmitter<{
    beforeInitialFetch: () => void;
  }>;

  constructor(params: CustomColumnsStateParams<T, F>) {
    this.table = params.table;
    this.container = params.container;

    this.columnsCollection = new CollectionState({
      ...this.container,
      events: {},
      queryName: [
        this.container.environment.componentId,
        this.table.collection.queryName,
        'customColumns',
      ]
        .filter(Boolean)
        .join('/'),
      fetchData: async () => {
        const { orderedColumns } = this;

        const items = orderedColumns.filter(
          (column) => column.hiddenFromCustomColumnsSelection !== true,
        );

        return {
          items,
        };
      },
      filters: {},
      itemKey: (item) => item.id,
      itemName: (item) => item.name,
      fetchErrorMessage: () => null,
    });

    if (params.dragAndDrop) {
      const { CustomColumnsDragAndDropState } = params.dragAndDrop;
      this.dragAndDrop = new CustomColumnsDragAndDropState({
        customColumns: this,
        components: params.dragAndDrop,
      });
    }

    this.customColumnsHistory = this.table.collection.persistQueryToUrl
      ? new CustomColumnsHistoryState({
          customColumns: this,
          history: params.container.history,
        })
      : null;

    makeObservable(this, {
      persistCustomColumns: action.bound,
      toSelectedColumns: computed,
      orderedColumns: computed,
      selectedOrderedColumns: computed,
      init: action,
    });
  }

  get orderedColumns(): Column[] {
    const { dragAndDrop, table } = this;
    const { columns, customColumnsProps } = table;

    if (customColumnsProps?.dragAndDrop && dragAndDrop) {
      return dragAndDrop.orderedColumns;
    }

    return columns;
  }

  get selectedOrderedColumns() {
    const {
      orderedColumns,
      table: {
        selectedColumns: { value: selectedColumns },
      },
    } = this;

    return orderedColumns.filter((column) =>
      selectedColumns.some(
        (selectedColumn) =>
          selectedColumn.id === column.id && selectedColumn.isSelected,
      ),
    );
  }

  async persistCustomColumns() {
    const { table } = this;
    const { dataCapsule, viewsProps } = table;

    // if views enabled columns persists inside the view instead of data capsule
    if (viewsProps) {
      return;
    }

    await dataCapsule.setItem('custom-columns', table.selectedColumns.value);
  }

  private registerSelectedColumnChange() {
    const { table } = this;
    table.selectedColumns.events.on('refresh', this.persistCustomColumns);

    return () => {
      table.selectedColumns.events.off('refresh', this.persistCustomColumns);
    };
  }

  mergeNewColumns() {
    const {
      table: { columns, selectedColumns, _toStoredColumn, customColumnsProps },
      _didFirstColumnsMerge,
    } = this;

    const defaultHidden =
      !_didFirstColumnsMerge && !!selectedColumns.value.length;

    const mergeTargetSelectedColumns = [...selectedColumns.value];

    if (customColumnsProps?.dragAndDrop == null) {
      return [
        ...mergeTargetSelectedColumns.filter((s) =>
          columns.every((c) => s.id !== c.id),
        ),
        ...columns.map((c) => {
          const existing = selectedColumns.value.find((s) => s.id === c.id);
          if (existing == null) {
            return _toStoredColumn(c);
          }

          return {
            ...existing,
            isSelected:
              c.hideable === false ||
              c.hiddenFromCustomColumnsSelection ||
              existing.isSelected,
          };
        }),
      ];
    }

    const diffColumns = columns
      .filter((c) => selectedColumns.value.every((s) => s.id !== c.id))
      .map((c) =>
        _toStoredColumn({
          ...c,
          defaultHidden: defaultHidden || c.defaultHidden,
        }),
      );

    // place new columns according to their order in the original columns prop
    diffColumns.forEach((d) => {
      const originalIndex = columns.findIndex((c) => c.id === d.id);
      const afterId = originalIndex > 0 ? columns[originalIndex - 1].id : null;
      const afterIndex =
        afterId != null
          ? mergeTargetSelectedColumns.findIndex((c) => c.id === afterId) + 1
          : originalIndex === 0
          ? 0
          : mergeTargetSelectedColumns.length;
      mergeTargetSelectedColumns.splice(afterIndex, 0, d);
    });

    this._didFirstColumnsMerge = true;
    return mergeTargetSelectedColumns;
  }

  onColumnCheckboxToggle = action((column: Column) => {
    const { select, onColumnClick } = this;
    select.toggle(column);
    onColumnClick?.(column, select.isChecked(column.id));
  });

  getStickyColumnsCount({ stickyColumns }: { stickyColumns: number }) {
    const {
      dragAndDrop,
      table: {
        selectedColumns: { value: selectedColumns },
      },
    } = this;

    if (dragAndDrop == null) {
      return stickyColumns;
    }

    const { reorderDisabled: reorderDisabledColumns } =
      dragAndDrop.orderedColumnsByReorderDisabled;

    if (!reorderDisabledColumns.length) {
      return stickyColumns;
    }

    return dragAndDrop.orderedColumnsByReorderDisabled.reorderDisabled.filter(
      (column) =>
        selectedColumns.some(
          (selectedColumn) =>
            selectedColumn.id === column.id && selectedColumn.isSelected,
        ),
    ).length;
  }

  get toSelectedColumns() {
    const {
      select,
      table: {
        selectedColumns: { value },
      },
    } = this;

    return value.map(({ id }) => ({
      id,
      isSelected: select.isChecked(id),
    }));
  }

  get selectionChanged() {
    const {
      toSelectedColumns,
      table: {
        selectedColumns: { value },
      },
    } = this;

    if (value.length !== toSelectedColumns.length) {
      return true;
    }

    for (let i = 0; i < value.length; i++) {
      const a = value[i];
      const b = toSelectedColumns[i];

      if (a.id !== b.id || a.isSelected !== b.isSelected) {
        return true;
      }
    }

    return false;
  }

  init() {
    const { table } = this;
    table.customColumnsStateInitialized = true;

    const disposers = [
      reaction(
        () => this.table.selectedColumns.value,
        async (selectedColumns) => {
          const { select, columnsCollection } = this;

          select.clear();
          select.forceSetMany(
            selectedColumns.map(({ id, isSelected }) => ({
              value: { id },
              status: isSelected ? select.finalStatus : select.startStatus,
            })),
          );
          await columnsCollection.refreshAllPages();
        },
        {
          fireImmediately: true,
        },
      ),
      reaction(
        () => this.select.selectedValues,
        () => {
          const {
            table: { selectedColumns },
          } = this;

          if (!this.selectionChanged) {
            return;
          }

          selectedColumns.setValue(this.toSelectedColumns, {
            emitEvents: ['change', 'beforeRefresh', 'refresh'],
          });
        },
      ),

      this.registerSelectedColumnChange(),
      this.columnsCollection.init(),
      this.dragAndDrop?.init(),
      this.customColumnsHistory?.init(),
    ];

    return () => {
      disposers.forEach((disposer) => disposer?.());
    };
  }

  async _getStoredCustomColumns() {
    const { dataCapsule } = this.table;
    try {
      return asStoredColumnsOrNull(await dataCapsule.getItem('custom-columns'));
    } catch (e) {
      if (e instanceof Error && e.message === 'Key was not found in capsule') {
        return null;
      }

      throw e;
    }
  }

  _tableInit() {
    const {
      initTask,
      table: {
        multi: { collections },
      },
    } = this;
    initTask.runOnce(async () => {
      try {
        const beforeInitialFetchListeners =
          this.events.listeners('beforeInitialFetch');
        await Promise.all(beforeInitialFetchListeners.map((l) => l()));
        await this._fetchFromStorage();
      } catch (e) {
        console.error(e);
      }
    });

    collections.forEach((collection) => {
      if (collection.query.refreshOnColumnsChange) {
        collection.emitter.addListener(
          'beforeInitialFetch',
          () => initTask.status.promise,
        );
      }
    });
  }

  async _fetchFromStorage() {
    const {
      table: {
        viewsProps,
        columns,
        customColumnsProps,
        selectedColumns,
        customColumnsState,
      },
    } = this;

    if (viewsProps) {
      return;
    }

    if (customColumnsState?.customColumnsHistory?._selectedColumnsFromUrl) {
      return;
    }

    const storedCustomColumns = await this._getStoredCustomColumns();

    if (storedCustomColumns == null) {
      return;
    }

    const selectedColumnsFromStorage = columns.map(
      ({ id, name, hideable, hiddenFromCustomColumnsSelection }) => ({
        id,
        name,
        isSelected:
          storedCustomColumns.some(
            (c) => c?.id === id && c?.isSelected !== false,
          ) ||
          !hideable ||
          hiddenFromCustomColumnsSelection === true,
      }),
    );

    selectedColumns.setValue(
      customColumnsProps?.dragAndDrop
        ? selectedColumnsFromStorage.sort(
            indexOfCompareFn(storedCustomColumns, (e) => e?.id),
          )
        : selectedColumnsFromStorage,
    );
  }

  customizeColumnsInSidePanelBi({
    actionName,
  }: {
    actionName: CustomColumnsActionName;
  }) {
    if (this._performedCustomColumnsActions.get(actionName)) {
      return;
    }
    this._performedCustomColumnsActions.set(actionName, true);
    this.table.reportBi(
      withoutDefaults(cairoCustomizeColumnsInSidePanel)({
        actionName,
      }),
    );
  }
}
